/**
 * Pricing a sale, and the bill rules around it: web v1's pricing closure
 * (`helpers/saleHelpers/index.desktop.ts`: `calculateSale`, `getAmountForCharge`,
 * `calculateCharges`, `shouldRetainBillDiscount`, the `customerRefId` fill and
 * `removeUnappliedCoupons`' choice), the ONE body on both platforms (rule 9). Pure apart from
 * rista-library, imported as a module.
 */
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";

import { isPointsLineAsV1 } from "../../promotions/entry";
import { pricingParams, type PricingState } from "./params";

declare function structuredClone<T>(value: T): T;

/** the server's mark on a sale that came from the API: its bill discounts are left alone */
const API_SOURCE = "API";
/** an automatic discount's mode: the library applies and removes those itself */
const AUTO_MODE = "Auto";

export const isApiSale = (sale: any): boolean => sale?.sourceInfo?.source === API_SOURCE;

/**
 * Whether a bill discount survives a repricing. Identifiable entries are kept, transient scratch
 * state from an in-progress edit is dropped:
 *
 * - `discountId`, a catalogue discount or deal;
 * - `mid`, a coupon or partner discount, which has no `discountId`;
 * - redeemed loyalty points (`isPointsLineAsV1`, today's rule held equal to v1's: decision 59), which have neither and must stay on the bill until their refund
 *   succeeds (`docs/flows/LOYALTY_REFUND.md`).
 *
 * Mobile v1 kept only `discountId`, dropping coupons and points (L33).
 */
export function shouldRetainBillDiscount(discount: any): boolean {
  return !!(discount?.discountId || discount?.mid || isPointsLineAsV1(discount));
}

/** an API sale's bill discounts as they are; anyone else's filtered */
export function retainedBillDiscounts(sale: any): any[] | undefined {
  return isApiSale(sale)
    ? sale?.billDiscountList
    : sale?.billDiscountList?.filter(shouldRetainBillDiscount);
}

/**
 * Price a sale with its own params.
 *
 * ⚠️ The NC (no-charge) branch MUTATES `sale` IN PLACE and returns it, where the normal branch
 * returns a NEW object from the library. Callers depend on both shapes; preserved.
 */
export function priceSale(sale: any, state: PricingState): any {
  const params = pricingParams(sale, state);
  if (sale.transactionType === "NC") {
    saleutil.calculateForNC(sale, params);
    return sale;
  }
  return saleutil.calculateForSale(sale, params);
}

/** one charge's amount: a full pricing of a copy of `record`, read back (web's own design) */
export function chargeAmount(charge: any, record: any, state: PricingState): number {
  const priced = priceSale(structuredClone(record), state);
  const match = priced?.chargeList?.find((c: any) => c.chargeId === charge.chargeId);
  return match ? match.amount : 0;
}

/**
 * Every charge on `sale`, re-amounted.
 *
 * ⚠️ Each charge is priced against `current`, the sale IN THE STORE, not against `sale`: web's own
 * behaviour (`calculateCharges` reads `getClonedSaleDataFromReducer()`), preserved for parity.
 */
export function withCharges(sale: any, current: any, state: PricingState): any {
  const chargeList = structuredClone(sale?.chargeList ?? []).map((charge: any) => ({
    ...charge,
    amount: chargeAmount(charge, current, state),
  }));
  return { ...sale, chargeList };
}

/** discounts read `customerRefId`; a customer that has only its `mid` gets it as the ref (mobile v1 had none, L33) */
export function withCustomerRefId(sale: any): any {
  const customer = sale?.customerInfo;
  if (!customer || customer.customerRefId || !customer.mid) return sale;
  return { ...sale, customerInfo: { ...customer, customerRefId: customer.mid } };
}

/**
 * The coupons a repricing dropped: a bill discount that is not automatic, was in the previous
 * bill's `discountList` and is not in this one's. They come off the bill (and are released at
 * the partner) by the caller; automatic discounts are the library's own (mobile v1 had no sweep, L33).
 */
export function unappliedCoupons(sale: any, previous: any): any[] {
  const now = new Set((sale?.discountList ?? []).map((d: any) => d.discountId));
  const before = new Set((previous?.discountList ?? []).map((d: any) => d.discountId));
  return (sale?.billDiscountList ?? []).filter(
    (d: any) => d.mode !== AUTO_MODE && !now.has(d.discountId) && before.has(d.discountId),
  );
}
